import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropzone", "fileInput", "tracksList", "uploadProgress", "sortableList", "expandIcon", "expandedRow", "trackFileInput", "saveButton", "saveButtonText", "saveButtonSpinner", "lyricsModal", "lyricsModalTextarea", "lyricsInput"]
  static values = { albumId: Number, minDuration: { type: Number, default: 3 }, initialErrors: { type: Object, default: {} } }

  connect() {
    this.draggedElement = null
    this.currentlyExpandedRow = null

    // Handle initial errors from server (e.g., from publish validation)
    if (this.initialErrorsValue && Object.keys(this.initialErrorsValue).length > 0) {
      // Convert server format { trackId: { title, errors } } to { trackId: [errors] }
      setTimeout(() => {
        const errorsByTrack = {}
        Object.entries(this.initialErrorsValue).forEach(([trackId, data]) => {
          errorsByTrack[trackId] = data.errors
        })
        this.showValidationErrors(errorsByTrack)
      }, 100)
    }
  }

  // Check audio file duration using Web Audio API
  async checkAudioDuration(file) {
    return new Promise((resolve, reject) => {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const reader = new FileReader()

      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
          const duration = audioBuffer.duration
          audioContext.close()
          resolve(duration)
        } catch (error) {
          audioContext.close()
          // If we can't decode, let the server handle validation
          resolve(null)
        }
      }

      reader.onerror = () => {
        resolve(null) // Let server handle if we can't read
      }

      reader.readAsArrayBuffer(file)
    })
  }

  // Use Stimulus action instead of manual event listeners
  trackFileInputTargetConnected(element) {
  }

  async handleTrackFileSelected(event) {
    const input = event.target
    const trackId = input.dataset.trackId
    const file = input.files[0]

    if (!file) {
      return
    }

    // Find the audio file container for this track to show errors there
    const expandedRow = Array.from(this.expandedRowTargets).find(row => row.dataset.trackId === trackId)
    const audioFileContainer = expandedRow?.querySelector('[data-audio-file-container]')

    // Validate duration before uploading
    const duration = await this.checkAudioDuration(file)
    if (duration !== null && duration < this.minDurationValue) {
      const errorMessage = window.I18n.t('albums.tracks.audio_too_short', {
        filename: file.name,
        minimum: this.minDurationValue,
        duration: duration.toFixed(1)
      })
      // Show error in the audio file container if available, otherwise fallback to global
      if (audioFileContainer) {
        this.showErrorInContainer(audioFileContainer, errorMessage)
      } else {
        this.showError(errorMessage)
      }
      input.value = ''
      delete input.dataset.replace
      return
    }

    // Check if this is a replace operation
    const isReplace = input.dataset.replace === 'true'

    // Upload the file for this specific track
    await this.uploadTrackFile(trackId, file, isReplace)

    // Reset the input and flag
    input.value = ''
    delete input.dataset.replace
  }

  async uploadTrackFile(trackId, file, replace = false) {
    const formData = new FormData()
    formData.append('track_file[file]', file)
    formData.append('track_id', trackId)
    if (replace) {
      formData.append('replace', 'true')
    }

    // Find the audio file container for this track
    const expandedRow = Array.from(this.expandedRowTargets).find(row => row.dataset.trackId === trackId)
    if (!expandedRow) {
      console.error('Could not find expanded row for track:', trackId)
      return
    }

    const audioFileContainer = expandedRow.querySelector('[data-audio-file-container]')
    if (!audioFileContainer) {
      console.error('Could not find audio file container')
      return
    }

    // Show progress bar
    this.showUploadProgressInContainer(audioFileContainer, file.name)

    try {
      const xhr = new XMLHttpRequest()

      // Upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100
          this.updateUploadProgress(audioFileContainer, percentComplete)
        }
      })

      // Handle completion
      xhr.addEventListener('load', () => {
        try {
          const result = JSON.parse(xhr.responseText)
          if (xhr.status === 200 && result.success) {
            this.showSuccess('Audio file uploaded successfully')
            // Update the UI with the new file info
            this.updateAudioFileWidget(audioFileContainer, result.track, trackId, result.track_file.id)

            // Update the file column and duration in the track list table
            const trackRow = this.sortableListTarget.querySelector(`tr[data-track-id="${trackId}"]:not([data-track-upload-target="expandedRow"])`)
            if (trackRow) {
              // Update file column
              const fileCell = trackRow.cells[4]
              if (fileCell) {
                fileCell.innerHTML = `<div class="text-sm text-gray-500">${result.track.file_name}</div>`
              }
              // Update duration column
              const durationCell = trackRow.cells[3]
              if (durationCell) {
                durationCell.innerHTML = `<div class="text-sm text-gray-500">${result.track.duration || '--:--'}</div>`
              }
            }
          } else {
            // Handle validation errors (422) or other failures
            this.hideUploadProgressInContainer(audioFileContainer)
            const errorMessage = result.errors?.join(', ') || window.I18n.t('albums.tracks.server_error')
            this.showErrorInContainer(audioFileContainer, `${window.I18n.t('albums.tracks.failed_upload')}: ${errorMessage}`)
          }
        } catch (e) {
          this.hideUploadProgressInContainer(audioFileContainer)
          this.showErrorInContainer(audioFileContainer, `${window.I18n.t('albums.tracks.failed_upload')}: ${window.I18n.t('albums.tracks.server_error')}`)
        }
      })

      // Handle errors
      xhr.addEventListener('error', () => {
        this.hideUploadProgressInContainer(audioFileContainer)
        this.showErrorInContainer(audioFileContainer, `${window.I18n.t('albums.tracks.failed_upload')}: ${window.I18n.t('albums.tracks.network_error')}`)
      })

      // Send request
      xhr.open('POST', `/albums/${this.albumIdValue}/track_files`)
      xhr.setRequestHeader('X-CSRF-Token', document.querySelector('meta[name="csrf-token"]').getAttribute('content'))
      xhr.send(formData)

    } catch (error) {
      console.error('Upload error:', error)
      this.hideUploadProgressInContainer(audioFileContainer)
      this.showErrorInContainer(audioFileContainer, `${window.I18n.t('albums.tracks.failed_upload')}: ${window.I18n.t('albums.tracks.network_error')}`)
    }
  }

  showUploadProgressInContainer(container, fileName) {
    container.innerHTML = `
      <div class="border border-gray-300 rounded-md p-3 bg-white">
        <div class="flex items-center space-x-3 mb-2">
          <svg class="h-6 w-6 text-indigo-600 animate-pulse" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900 truncate">${fileName}</p>
            <p class="text-xs text-gray-500">${window.I18n.t('albums.tracks.uploading')}</p>
          </div>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-2">
          <div class="bg-indigo-600 h-2 rounded-full transition-all duration-300" style="width: 0%" data-progress-bar></div>
        </div>
      </div>
    `
  }

  updateUploadProgress(container, percent) {
    const progressBar = container.querySelector('[data-progress-bar]')
    if (progressBar) {
      progressBar.style.width = `${percent}%`
    }
  }

  hideUploadProgressInContainer(container) {
    // Show the upload dropzone again
    container.innerHTML = `
      <div class="border-2 border-dashed border-gray-300 rounded-md p-4 text-center bg-gray-50 hover:border-gray-400 transition-colors cursor-pointer" data-action="click->track-upload#selectUploadFile" data-track-id="${container.closest('[data-track-id]').dataset.trackId}">
        <svg class="mx-auto h-8 w-8 text-gray-400 mb-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <p class="text-xs text-gray-600 font-medium">Click to upload audio file</p>
        <p class="text-xs text-gray-500 mt-0.5">WAV or FLAC format</p>
      </div>
    `
  }

  updateAudioFileWidget(container, track, trackId, trackFileId) {
    // Create full audio file widget with player
    container.innerHTML = `
      <div class="border border-gray-300 rounded-md p-3 bg-white">
        <div class="flex items-center space-x-3 mb-2">
          <svg class="h-6 w-6 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
          </svg>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900 truncate">${track.file_name}</p>
            <p class="text-xs text-gray-500">${track.file_size || ''} • ${track.duration || ''}</p>
          </div>
        </div>
        <div class="mb-2">
          <audio controls class="w-full" style="height: 32px;">
            <source src="${track.file_url}" type="${track.file_content_type}">
            Your browser does not support the audio element.
          </audio>
        </div>
        <div class="flex space-x-2">
          <button type="button" class="flex-1 px-2 py-1.5 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors cursor-pointer" data-action="click->track-upload#selectReplaceFile" data-track-id="${trackId}">
            Replace File
          </button>
          <button type="button" class="flex-1 px-2 py-1.5 border border-red-300 rounded-md shadow-sm text-xs font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors cursor-pointer" data-action="click->track-upload#deleteFile" data-track-file-id="${trackFileId}" data-track-id="${trackId}">
            Delete File
          </button>
        </div>
      </div>
    `
  }

  // Handle drag events
  dragover(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add("border-indigo-500", "bg-indigo-50")
  }

  dragleave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-indigo-500", "bg-indigo-50")
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-indigo-500", "bg-indigo-50")
    
    const files = Array.from(event.dataTransfer.files)
    this.handleFiles(files)
  }

  // Handle click to select files
  selectFiles() {
    this.fileInputTarget.click()
  }

  // Handle file input change
  fileSelected(event) {
    const files = Array.from(event.target.files)
    this.handleFiles(files)
    // Reset file input
    event.target.value = ""
  }

  // Process uploaded files
  async handleFiles(files) {
    // Filter for audio files only
    const audioFiles = files.filter(file =>
      file.type === 'audio/wav' ||
      file.type === 'audio/x-wav' ||
      file.type === 'audio/flac' ||
      file.name.toLowerCase().endsWith('.wav') ||
      file.name.toLowerCase().endsWith('.flac')
    )

    if (audioFiles.length === 0) {
      this.showError(window.I18n.t('albums.tracks.select_wav_flac'))
      return
    }

    // Validate duration for all files before uploading
    const validFiles = []
    for (const file of audioFiles) {
      const duration = await this.checkAudioDuration(file)
      if (duration !== null && duration < this.minDurationValue) {
        this.showError(window.I18n.t('albums.tracks.audio_too_short', {
          filename: file.name,
          minimum: this.minDurationValue,
          duration: duration.toFixed(1)
        }))
        continue
      }
      validFiles.push(file)
    }

    if (validFiles.length === 0) {
      return
    }

    // Upload files one by one with progress indication
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i]
      const fileNumber = i + 1
      const totalFiles = validFiles.length

      // Show which file is being uploaded (e.g., "Uploading track1.wav (1 of 3)...")
      await this.uploadFile(file, fileNumber, totalFiles)
    }
  }

  async uploadFile(file, fileNumber = 1, totalFiles = 1) {
    const formData = new FormData()
    formData.append("track_file[file]", file)

    // Show upload progress with file count
    this.showUploadProgress(file.name, fileNumber, totalFiles)

    try {
      const response = await fetch(`/albums/${this.albumIdValue}/track_files`, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content")
        }
      })

      const result = await response.json()

      if (result.success) {
        this.addTrackToList(result.html)
        this.hideUploadProgress()
      } else {
        this.showError(`Failed to upload ${file.name}: ${result.errors?.join(', ') || 'Unknown error'}`)
        this.hideUploadProgress()
      }
    } catch (error) {
      console.error("Upload error:", error)
      this.showError(`Failed to upload ${file.name}: Network error`)
      this.hideUploadProgress()
    }
  }

  addTrackToList(html) {
    // If no tracks exist, create the tracks table
    const tracksTable = this.tracksListTarget.querySelector('tbody')
    if (!tracksTable) {
      this.createTracksTable()
    }

    // Insert the server-rendered HTML (both track row and expanded row)
    const tbody = this.tracksListTarget.querySelector('tbody')
    if (tbody) {
      tbody.insertAdjacentHTML('beforeend', html.track_row + html.expanded_row)
    }

    // Update track count
    this.updateTrackCount()
  }

  createTracksTable() {
    const t = window.I18n.t.bind(window.I18n)
    const tableHTML = `
      <h3 class="text-lg font-medium text-gray-900 mb-4">${t('albums.tracks.track_listing')}</h3>
      <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="w-12 px-2 py-3"></th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ${t('albums.tracks.track')}
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ${t('albums.tracks.title')}
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ${t('albums.tracks.duration')}
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ${t('albums.tracks.file')}
              </th>
              <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                ${t('albums.tracks.actions')}
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200" data-track-upload-target="sortableList">
          </tbody>
        </table>
      </div>
    `
    this.tracksListTarget.innerHTML = tableHTML
  }

  async deleteTrack(event) {
    event.stopPropagation()

    const trackId = event.currentTarget.dataset.trackId
    const trackTitle = event.currentTarget.dataset.trackTitle


    if (!trackId) {
      this.showError(window.I18n.t('albums.tracks.track_id_missing'))
      return
    }

    if (!confirm(window.I18n.t('albums.tracks.confirm_delete_track', { title: trackTitle }))) {
      return
    }

    const deleteUrl = `/albums/${this.albumIdValue}/tracks/${trackId}`

    try {
      const response = await fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        // Remove both track rows from UI (main row and expandable edit row)
        const trackRows = document.querySelectorAll(`tr[data-track-id="${trackId}"]`)
        trackRows.forEach(row => row.remove())

        // Check if this was the last track
        const remainingTracks = this.sortableListTarget.querySelectorAll('tr[data-track-id]:not([data-track-upload-target="expandedRow"])')

        if (remainingTracks.length === 0) {
          // Remove the entire tracks table when no tracks remain
          const tracksTable = this.tracksListTarget.querySelector('.bg-white.border')
          if (tracksTable) {
            tracksTable.remove()
          }
        } else {
          // Renumber remaining tracks
          this.updateTrackNumbers()

          // Save new order to server
          this.saveTrackOrder()
        }

        this.updateTrackCount()
      } else {
        this.showError(window.I18n.t('albums.tracks.failed_delete_track'))
      }
    } catch (error) {
      console.error("Delete error:", error)
      this.showError(window.I18n.t('albums.tracks.failed_delete_track'))
    }
  }

  showUploadProgress(fileName, fileNumber = 1, totalFiles = 1) {
    if (this.hasUploadProgressTarget) {
      const progressText = totalFiles > 1
        ? window.I18n.t('albums.tracks.uploading_file_progress', { filename: fileName, current: fileNumber, total: totalFiles })
        : window.I18n.t('albums.tracks.uploading_file', { filename: fileName })

      this.uploadProgressTarget.innerHTML = `
        <div class="flex items-center space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span class="text-sm text-blue-800">${progressText}</span>
        </div>
      `
      this.uploadProgressTarget.classList.remove('hidden')
    }
  }

  hideUploadProgress() {
    if (this.hasUploadProgressTarget) {
      this.uploadProgressTarget.classList.add('hidden')
    }
  }

  showError(message) {
    // Check if dropzone target exists
    if (!this.hasDropzoneTarget) {
      console.error(message)
      return
    }

    // Create error message
    const errorDiv = document.createElement('div')
    errorDiv.className = 'rounded-md bg-red-50 p-4 mb-4'
    errorDiv.innerHTML = `
      <div class="flex justify-between items-start">
        <div class="ml-3 text-sm text-red-700">
          ${message}
        </div>
        <button onclick="this.closest('.rounded-md').remove()" class="ml-4 inline-flex text-gray-400 hover:text-gray-500 focus:outline-none transition-colors cursor-pointer">
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    `

    // Insert at top of dropzone
    this.dropzoneTarget.parentNode.insertBefore(errorDiv, this.dropzoneTarget)

    // Remove after 5 seconds
    setTimeout(() => {
      errorDiv.remove()
    }, 5000)
  }

  showErrorInContainer(container, message) {
    // Remove any existing error in this container
    const existingError = container.querySelector('[data-error-message]')
    if (existingError) {
      existingError.remove()
    }

    // Create error message
    const errorDiv = document.createElement('div')
    errorDiv.setAttribute('data-error-message', '')
    errorDiv.className = 'rounded-md bg-red-50 p-3 mb-2'
    errorDiv.innerHTML = `
      <div class="flex justify-between items-start">
        <div class="ml-3 text-sm text-red-700">
          ${message}
        </div>
        <button onclick="this.closest('[data-error-message]').remove()" class="ml-4 inline-flex text-gray-400 hover:text-gray-500 focus:outline-none transition-colors cursor-pointer">
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    `

    // Insert at top of container
    container.insertBefore(errorDiv, container.firstChild)

    // Scroll into view
    errorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' })

    // Remove after 8 seconds
    setTimeout(() => {
      errorDiv.remove()
    }, 8000)
  }

  updateTrackCount() {
    // Update any track count displays
    const trackRows = this.hasSortableListTarget
      ? this.sortableListTarget.querySelectorAll('tr[data-track-id]:not([data-track-upload-target="expandedRow"])')
      : []
    const trackCount = trackRows.length

    const countElements = document.querySelectorAll('[data-track-count]')
    countElements.forEach(el => {
      el.textContent = `${trackCount} track${trackCount !== 1 ? 's' : ''}`
    })

    // Update UI based on track count (dropzone and save button)
    this.updateUIBasedOnTrackCount(trackCount)
  }

  updateUIBasedOnTrackCount(trackCount) {
    if (trackCount === 0) {
      // No tracks: show large dropzone and disable save button
      this.switchToLargeDropzone()
      this.disableSaveButton()
    } else if (trackCount === 1) {
      // First track added: show compact dropzone and enable save button
      this.switchToCompactDropzone()
      this.enableSaveButton()
    }
    // For trackCount > 1, dropzone and button stay in their current state (compact/enabled)
  }

  enableSaveButton() {
    if (!this.hasSaveButtonTarget) return

    // Remove disabled styling
    this.saveButtonTarget.classList.remove('bg-gray-300', 'cursor-not-allowed', 'pointer-events-none')

    // Add enabled styling
    this.saveButtonTarget.classList.add('bg-green-600', 'hover:bg-green-700', 'focus:ring-green-500', 'cursor-pointer')

    // Update button text
    if (this.hasSaveButtonTextTarget) {
      this.saveButtonTextTarget.textContent = 'SAVE UNPUBLISHED CHANGES AND CONTINUE →'
    }
  }

  disableSaveButton() {
    if (!this.hasSaveButtonTarget) return

    // Remove enabled styling
    this.saveButtonTarget.classList.remove('bg-green-600', 'hover:bg-green-700', 'focus:ring-green-500', 'cursor-pointer')

    // Add disabled styling
    this.saveButtonTarget.classList.add('bg-gray-300', 'cursor-not-allowed', 'pointer-events-none')

    // Update button text
    if (this.hasSaveButtonTextTarget) {
      this.saveButtonTextTarget.textContent = 'ADD TRACKS TO CONTINUE'
    }
  }

  switchToLargeDropzone() {
    // Update classes for large dropzone (p-8 padding)
    this.dropzoneTarget.className = "border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"

    // Replace content while preserving Stimulus actions
    this.dropzoneTarget.setAttribute('data-action', 'dragover->track-upload#dragover dragleave->track-upload#dragleave drop->track-upload#drop click->track-upload#selectFiles')
    this.dropzoneTarget.innerHTML = `
      <svg class="mx-auto h-10 w-10 text-gray-400 mb-3" stroke="currentColor" fill="none" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
      </svg>

      <h3 class="text-base font-medium text-gray-900 mb-1">${window.I18n.t('albums.tracks.drag_audio_files_here')}</h3>
      <p class="text-sm text-gray-600">${window.I18n.t('albums.tracks.click_to_browse_files')}</p>

      <input type="file"
             data-track-upload-target="fileInput"
             data-action="change->track-upload#fileSelected"
             multiple
             accept=".wav,.flac,audio/wav,audio/x-wav,audio/flac"
             class="hidden">
    `
  }

  switchToCompactDropzone() {
    // Update classes for compact dropzone (p-4 padding)
    this.dropzoneTarget.className = "border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors cursor-pointer"

    // Replace content while preserving Stimulus actions
    this.dropzoneTarget.setAttribute('data-action', 'dragover->track-upload#dragover dragleave->track-upload#dragleave drop->track-upload#drop click->track-upload#selectFiles')
    this.dropzoneTarget.innerHTML = `
      <div class="flex items-center justify-center space-x-3">
        <svg class="h-6 w-6 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
        <div>
          <span class="text-sm font-medium text-gray-900">${window.I18n.t('albums.tracks.upload_additional_tracks')}</span>
          <span class="text-sm text-gray-600 ml-2">${window.I18n.t('albums.tracks.click_to_browse')}</span>
        </div>
      </div>

      <input type="file"
             data-track-upload-target="fileInput"
             data-action="change->track-upload#fileSelected"
             multiple
             accept=".wav,.flac,audio/wav,audio/x-wav,audio/flac"
             class="hidden">
    `
  }

  // Enable dragging when handle is clicked
  enableDrag(event) {
    const row = event.currentTarget.closest('tr')
    row.setAttribute('draggable', 'true')
    row.addEventListener('dragstart', this.dragStart.bind(this))
    row.addEventListener('dragend', this.dragEnd.bind(this))
  }

  // Drag and drop methods for reordering tracks
  dragStart(event) {
    this.draggedElement = event.currentTarget
    this.draggedElement.style.opacity = '0.4'
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/html', this.draggedElement.innerHTML)
  }

  dragOver(event) {
    if (event.preventDefault) {
      event.preventDefault()
    }

    const target = event.currentTarget
    if (target !== this.draggedElement && target.tagName === 'TR') {
      const rect = target.getBoundingClientRect()
      const midpoint = rect.top + rect.height / 2

      if (event.clientY < midpoint) {
        target.parentNode.insertBefore(this.draggedElement, target)
      } else {
        target.parentNode.insertBefore(this.draggedElement, target.nextSibling)
      }
    }

    event.dataTransfer.dropEffect = 'move'
    return false
  }

  dragEnd(event) {
    event.currentTarget.style.opacity = '1'
    // Disable dragging after drop
    event.currentTarget.setAttribute('draggable', 'false')

    // Update track numbers based on new order
    this.updateTrackNumbers()

    // Save new order to server
    this.saveTrackOrder()
  }

  dropTrack(event) {
    if (event.stopPropagation) {
      event.stopPropagation()
    }
    return false
  }

  updateTrackNumbers() {
    const rows = this.sortableListTarget.querySelectorAll('tr[data-track-id]:not([data-track-upload-target="expandedRow"])')
    rows.forEach((row, index) => {
      const trackNumberBadge = row.querySelector('.bg-indigo-100')
      if (trackNumberBadge) {
        trackNumberBadge.textContent = index + 1
      }
    })
  }

  async saveTrackOrder() {
    const rows = this.sortableListTarget.querySelectorAll('tr[data-track-id]:not([data-track-upload-target="expandedRow"])')
    const trackOrder = Array.from(rows).map((row, index) => ({
      id: parseInt(row.dataset.trackId),
      track_number: index + 1
    }))

    try {
      const response = await fetch(`/albums/${this.albumIdValue}/tracks/reorder`, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ tracks: trackOrder })
      })

      if (!response.ok) {
        this.showError(window.I18n.t('albums.tracks.failed_save_order'))
      }
    } catch (error) {
      console.error("Error saving track order:", error)
      this.showError(window.I18n.t('albums.tracks.failed_save_order'))
    }
  }

  // Stop event propagation (for drag handle)
  stopPropagation(event) {
    event.stopPropagation()
  }

  // Toggle expand/collapse track edit form
  toggleExpand(event) {
    event.stopPropagation()

    const trackRow = event.currentTarget.closest('tr[data-track-id]:not([data-track-upload-target="expandedRow"])')
    if (!trackRow) return

    const trackId = trackRow.dataset.trackId

    // Find the expanded row that matches this track ID
    const expandedRow = Array.from(this.expandedRowTargets).find(row => row.dataset.trackId === trackId)

    if (!expandedRow) return

    // Close any currently expanded row
    if (this.currentlyExpandedRow && this.currentlyExpandedRow !== expandedRow) {
      this.currentlyExpandedRow.classList.add('hidden')
      const prevTrackId = this.currentlyExpandedRow.dataset.trackId
      const prevTrackRow = this.sortableListTarget.querySelector(`tr[data-track-id="${prevTrackId}"]:not([data-track-upload-target="expandedRow"])`)
      const prevIcon = prevTrackRow?.querySelector('[data-track-upload-target="expandIcon"] svg')
      if (prevIcon) {
        prevIcon.classList.remove('rotate-180')
      }
    }

    // Toggle current row
    const isExpanded = !expandedRow.classList.contains('hidden')

    if (isExpanded) {
      expandedRow.classList.add('hidden')
      this.currentlyExpandedRow = null
    } else {
      expandedRow.classList.remove('hidden')
      this.currentlyExpandedRow = expandedRow
    }

    // Rotate chevron icon
    const icon = trackRow.querySelector('[data-track-upload-target="expandIcon"] svg')
    if (icon) {
      icon.classList.toggle('rotate-180')
    }
  }

  // Update track via AJAX
  async updateTrack(event) {
    event.preventDefault()

    const form = event.currentTarget
    const trackId = form.dataset.trackId
    const formData = new FormData(form)

    // Convert FormData to nested JSON structure
    const trackData = {}
    formData.forEach((value, key) => {
      // Handle simple fields: track[field]
      const simpleMatch = key.match(/^track\[(\w+)\]$/)
      if (simpleMatch) {
        trackData[simpleMatch[1]] = value
        return
      }

      // Handle array fields: track[field][]
      const arrayMatch = key.match(/^track\[(\w+)\]\[\]$/)
      if (arrayMatch) {
        const fieldName = arrayMatch[1]
        if (!trackData[fieldName]) {
          trackData[fieldName] = []
        }
        trackData[fieldName].push(value)
        return
      }

      // Handle nested contributor_roles: track[contributor_roles][role_key][artist_ids][]
      const nestedMatch = key.match(/^track\[contributor_roles\]\[(\w+)\]\[artist_ids\]\[\]$/)
      if (nestedMatch) {
        const roleKey = nestedMatch[1]
        if (!trackData.contributor_roles) {
          trackData.contributor_roles = {}
        }
        if (!trackData.contributor_roles[roleKey]) {
          trackData.contributor_roles[roleKey] = { artist_ids: [] }
        }
        trackData.contributor_roles[roleKey].artist_ids.push(value)
        return
      }
    })

    try {
      const response = await fetch(`/albums/${this.albumIdValue}/tracks/${trackId}`, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ track: trackData })
      })

      const result = await response.json()

      if (response.ok && result.success) {
        // Remove validation errors if they exist
        const existingError = document.querySelector('[data-validation-errors]')
        if (existingError) {
          existingError.remove()
        }

        // Update the track row with new data
        const trackRow = form.closest('tr[data-track-upload-target="expandedRow"]').previousElementSibling
        const titleCell = trackRow.querySelector('.text-sm.font-medium.text-gray-900')
        if (titleCell) {
          titleCell.textContent = trackData.title
        }

        // Close the expanded row
        const expandedRow = form.closest('tr[data-track-upload-target="expandedRow"]')
        expandedRow.classList.add('hidden')
        this.currentlyExpandedRow = null

        // Rotate icon back
        const icon = trackRow.querySelector('[data-track-upload-target="expandIcon"] svg')
        if (icon) {
          icon.classList.remove('rotate-180')
        }

        // Clear any error highlighting from this track
        expandedRow.querySelectorAll('.border-red-500').forEach(field => {
          field.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
          field.classList.add('border-gray-300', 'focus:border-indigo-500', 'focus:ring-indigo-500')
        })

        // Clear error styling from TomSelect fields
        expandedRow.querySelectorAll('.ts-control.has-error').forEach(control => {
          control.classList.remove('has-error')
        })

        // Clear contributor section highlighting
        const contributorSections = expandedRow.querySelectorAll('[data-contributor-section]')
        contributorSections.forEach(section => {
          const heading = section.querySelector('h4')
          if (heading) {
            heading.classList.remove('text-red-700')
          }
          const dropdown = section.querySelector('select')
          if (dropdown && dropdown.tomselect) {
            dropdown.tomselect.control.classList.remove('has-error')
          }
        })

        // Show success message briefly
        this.showSuccess(window.I18n.t('albums.tracks.track_updated_successfully'))
      } else {
        // Show validation errors at top of page
        const errors = result.errors || [window.I18n.t('albums.tracks.failed_update_track')]
        this.showTrackSaveErrors(trackId, errors)
      }
    } catch (error) {
      console.error("Update error:", error)
      this.showTrackSaveErrors(trackId, [window.I18n.t('albums.tracks.failed_update_track')])
    }
  }

  showSuccess(message) {
    // Remove all existing messages (errors and success)
    document.querySelectorAll('[data-track-save-errors], [data-validation-errors], [data-success-message]').forEach(el => el.remove())

    const successDiv = document.createElement('div')
    successDiv.setAttribute('data-success-message', '')
    successDiv.className = 'rounded-md bg-green-50 p-4 mb-4'
    successDiv.innerHTML = `
      <div class="text-sm text-green-700">
        ${message}
      </div>
    `

    // Insert before the tracks list
    this.tracksListTarget.parentNode.insertBefore(successDiv, this.tracksListTarget)

    setTimeout(() => {
      successDiv.remove()
    }, 3000)
  }

  showTrackSaveErrors(trackId, errors) {
    // Remove all existing messages (errors and success)
    document.querySelectorAll('[data-track-save-errors], [data-validation-errors], [data-success-message]').forEach(el => el.remove())

    // Get track title
    const trackRow = this.sortableListTarget.querySelector(`tr[data-track-id="${trackId}"]:not([data-track-upload-target="expandedRow"])`)
    const trackTitle = trackRow?.querySelector('.text-sm.font-medium.text-gray-900')?.textContent || `Track ${trackId}`

    const totalErrors = errors.length
    const validationMessage = totalErrors === 1
      ? window.I18n.t('albums.tracks.validation_errors_one', { count: totalErrors })
      : window.I18n.t('albums.tracks.validation_errors_other', { count: totalErrors })

    let errorList = errors.map(error => `<li>- ${error}</li>`).join('')

    const errorSummary = document.createElement('div')
    errorSummary.setAttribute('data-track-save-errors', '')
    errorSummary.className = 'rounded-md bg-red-50 p-4 mb-6'
    errorSummary.innerHTML = `
      <div class="flex justify-between items-start">
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">
            ${validationMessage}
          </h3>
          <div class="mt-2 text-sm text-red-700">
            <p class="font-medium">${trackTitle}:</p>
            <ul class="list-none space-y-1 mt-1">
              ${errorList}
            </ul>
          </div>
        </div>
        <button onclick="this.closest('[data-track-save-errors]').remove()" class="ml-4 inline-flex text-gray-400 hover:text-gray-500 focus:outline-none transition-colors cursor-pointer">
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    `

    // Insert after the album widget
    const albumWidget = document.querySelector('.bg-white.shadow.rounded-lg.mb-6.p-4')
    if (albumWidget) {
      albumWidget.insertAdjacentElement('afterend', errorSummary)
    } else {
      // Fallback: insert at top of this controller's element
      this.element.insertBefore(errorSummary, this.element.firstChild)
    }

    // Scroll to error summary
    errorSummary.scrollIntoView({ behavior: 'smooth', block: 'start' })

    // Expand track and highlight fields with errors
    this.expandAndHighlightTrack(trackId, errors)
  }

  // Generate year options for copyright year dropdowns
  generateYearOptions() {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let year = currentYear + 5; year >= 1900; year--) {
      const selected = year === currentYear ? 'selected' : ''
      years.push(`<option value="${year}" ${selected}>${year}</option>`)
    }
    return years.join('')
  }

  // File upload/replace methods
  selectUploadFile(event) {
    event.stopPropagation()
    const trackId = event.currentTarget.dataset.trackId
    const fileInput = this.trackFileInputTargets.find(input => input.dataset.trackId === trackId)
    if (fileInput) {
      fileInput.click()
    }
  }

  selectReplaceFile(event) {
    event.stopPropagation()
    const trackId = event.currentTarget.dataset.trackId
    const fileInput = this.trackFileInputTargets.find(input => input.dataset.trackId === trackId)
    if (fileInput) {
      fileInput.dataset.replace = 'true'
      fileInput.click()
    }
  }

  async deleteFile(event) {
    event.stopPropagation()
    const trackFileId = event.currentTarget.dataset.trackFileId
    const trackId = event.currentTarget.dataset.trackId

    if (!confirm('Are you sure you want to delete this audio file? The track metadata will remain.')) {
      return
    }

    try {
      const response = await fetch(`/albums/${this.albumIdValue}/track_files/${trackFileId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        this.showSuccess('Audio file deleted successfully')

        // Find the audio file container and show upload dropzone
        const expandedRow = Array.from(this.expandedRowTargets).find(row => row.dataset.trackId === trackId)
        if (expandedRow) {
          const audioFileContainer = expandedRow.querySelector('[data-audio-file-container]')
          if (audioFileContainer) {
            this.hideUploadProgressInContainer(audioFileContainer)
          }
        }

        // Update the file column and duration in the track list table
        const trackRow = this.sortableListTarget.querySelector(`tr[data-track-id="${trackId}"]:not([data-track-upload-target="expandedRow"])`)
        if (trackRow) {
          // Update file column
          const fileCell = trackRow.cells[4]
          if (fileCell) {
            fileCell.innerHTML = `<div class="text-sm text-gray-500">${window.I18n.t('albums.tracks.no_file')}</div>`
          }
          // Update duration column to placeholder
          const durationCell = trackRow.cells[3]
          if (durationCell) {
            durationCell.innerHTML = '<div class="text-sm text-gray-500">--:--</div>'
          }
        }
      } else {
        this.showError(window.I18n.t('albums.tracks.failed_delete_audio'))
      }
    } catch (error) {
      console.error('Delete file error:', error)
      this.showError('Failed to delete audio file')
    }
  }

  // Lyrics modal methods
  openLyricsModal(event) {
    event.stopPropagation()
    const trackId = event.currentTarget.dataset.trackId

    // Find the hidden lyrics input for this track
    const lyricsInput = this.lyricsInputTargets.find(input => input.dataset.trackId === trackId)
    if (!lyricsInput) {
      console.error('Could not find lyrics input for track:', trackId)
      return
    }

    // Store the current track ID for saving later
    this.currentLyricsTrackId = trackId

    // Populate the modal textarea with current lyrics
    this.lyricsModalTextareaTarget.value = lyricsInput.value

    // Show the modal
    this.lyricsModalTarget.classList.remove('hidden')

    // Focus on the textarea
    setTimeout(() => {
      this.lyricsModalTextareaTarget.focus()
    }, 100)
  }

  closeLyricsModal() {
    this.lyricsModalTarget.classList.add('hidden')
    this.currentLyricsTrackId = null
  }

  saveLyrics() {
    if (!this.currentLyricsTrackId) return

    // Find the hidden lyrics input for this track
    const lyricsInput = this.lyricsInputTargets.find(input => input.dataset.trackId === this.currentLyricsTrackId)
    if (!lyricsInput) {
      console.error('Could not find lyrics input for track:', this.currentLyricsTrackId)
      return
    }

    // Update the hidden input with the new lyrics
    lyricsInput.value = this.lyricsModalTextareaTarget.value

    // Update the button text to reflect whether lyrics exist
    const button = document.querySelector(`button[data-track-id="${this.currentLyricsTrackId}"][data-action="click->track-upload#openLyricsModal"]`)
    if (button) {
      button.textContent = lyricsInput.value.trim() ? window.I18n.t('albums.tracks.edit_lyrics') : window.I18n.t('albums.tracks.add_lyrics')
    }

    // Close the modal
    this.closeLyricsModal()
  }

  // Validate all tracks before navigating to review page
  async validateAllTracks(event) {
    event.preventDefault()

    // Show loading state
    this.setSaveButtonLoading(true)

    try {
      const response = await fetch(`/albums/${this.albumIdValue}/validate_tracks`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok && result.success) {
        // All tracks valid, navigate to review page
        window.location.href = `/albums/${this.albumIdValue}/review`
      } else {
        // Validation failed, show errors and hide loading state
        this.showValidationErrors(result.errors)
        this.setSaveButtonLoading(false)
      }
    } catch (error) {
      console.error('Validation error:', error)
      this.showError(window.I18n.t('albums.tracks.failed_validate'))
      this.setSaveButtonLoading(false)
    }
  }

  setSaveButtonLoading(loading) {
    if (!this.hasSaveButtonTarget) return

    if (loading) {
      // Disable button and show spinner
      this.saveButtonTarget.disabled = true
      this.saveButtonTarget.classList.add('opacity-75', 'cursor-not-allowed')
      this.saveButtonTarget.classList.remove('hover:bg-green-700', 'cursor-pointer')

      if (this.hasSaveButtonSpinnerTarget) {
        this.saveButtonSpinnerTarget.classList.remove('hidden')
      }

      if (this.hasSaveButtonTextTarget) {
        this.saveButtonTextTarget.textContent = 'Saving...'
      }
    } else {
      // Enable button and hide spinner
      this.saveButtonTarget.disabled = false
      this.saveButtonTarget.classList.remove('opacity-75', 'cursor-not-allowed')
      this.saveButtonTarget.classList.add('hover:bg-green-700', 'cursor-pointer')

      if (this.hasSaveButtonSpinnerTarget) {
        this.saveButtonSpinnerTarget.classList.add('hidden')
      }

      if (this.hasSaveButtonTextTarget) {
        this.saveButtonTextTarget.textContent = 'SAVE UNPUBLISHED CHANGES AND CONTINUE →'
      }
    }
  }

  showValidationErrors(errorsByTrack) {
    // Remove all existing messages (errors and success)
    document.querySelectorAll('[data-track-save-errors], [data-validation-errors], [data-success-message]').forEach(el => el.remove())

    // Count total errors
    const trackIds = Object.keys(errorsByTrack)
    const totalErrors = trackIds.reduce((sum, trackId) => sum + errorsByTrack[trackId].length, 0)

    // Create error summary
    const errorSummary = document.createElement('div')
    errorSummary.setAttribute('data-validation-errors', '')
    errorSummary.className = 'rounded-md bg-red-50 p-4 mb-6'

    let errorList = ''
    trackIds.forEach(trackId => {
      const trackRow = this.sortableListTarget.querySelector(`tr[data-track-id="${trackId}"]:not([data-track-upload-target="expandedRow"])`)
      const trackTitle = trackRow?.querySelector('.text-sm.font-medium.text-gray-900')?.textContent || `Track ${trackId}`
      errorList += `<li class="font-medium">${trackTitle}:</li>`
      errorsByTrack[trackId].forEach(error => {
        errorList += `<li class="ml-4">- ${error}</li>`
      })
    })

    const validationMessage = totalErrors === 1
      ? window.I18n.t('albums.tracks.validation_errors_one', { count: totalErrors })
      : window.I18n.t('albums.tracks.validation_errors_other', { count: totalErrors })

    errorSummary.innerHTML = `
      <div class="flex justify-between items-start">
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">
            ${validationMessage}
          </h3>
          <div class="mt-2 text-sm text-red-700">
            <ul class="list-none space-y-1">
              ${errorList}
            </ul>
          </div>
        </div>
        <button onclick="this.closest('[data-validation-errors]').remove()" class="ml-4 inline-flex text-gray-400 hover:text-gray-500 focus:outline-none transition-colors cursor-pointer">
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    `

    // Insert error summary after the album widget
    const albumWidget = document.querySelector('.bg-white.shadow.rounded-lg.mb-6.p-4')
    if (albumWidget) {
      albumWidget.insertAdjacentElement('afterend', errorSummary)
    } else {
      // Fallback: insert at top of this controller's element
      this.element.insertBefore(errorSummary, this.element.firstChild)
    }

    // Scroll to error summary
    errorSummary.scrollIntoView({ behavior: 'smooth', block: 'start' })

    // Expand tracks with errors and highlight fields
    trackIds.forEach(trackId => {
      this.expandAndHighlightTrack(trackId, errorsByTrack[trackId])
    })
  }

  expandAndHighlightTrack(trackId, errors) {
    // Find the track row
    const trackRow = this.sortableListTarget.querySelector(`tr[data-track-id="${trackId}"]:not([data-track-upload-target="expandedRow"])`)
    if (!trackRow) return

    // Find the expanded row
    const expandedRow = Array.from(this.expandedRowTargets).find(row => row.dataset.trackId === trackId)
    if (!expandedRow) return

    // Expand the row if not already expanded
    if (expandedRow.classList.contains('hidden')) {
      // Close any currently expanded row
      if (this.currentlyExpandedRow) {
        this.currentlyExpandedRow.classList.add('hidden')
        const prevTrackId = this.currentlyExpandedRow.dataset.trackId
        const prevTrackRow = this.sortableListTarget.querySelector(`tr[data-track-id="${prevTrackId}"]:not([data-track-upload-target="expandedRow"])`)
        const prevIcon = prevTrackRow?.querySelector('[data-track-upload-target="expandIcon"] svg')
        if (prevIcon) {
          prevIcon.classList.remove('rotate-180')
        }
      }

      expandedRow.classList.remove('hidden')
      this.currentlyExpandedRow = expandedRow

      // Rotate chevron icon
      const icon = trackRow.querySelector('[data-track-upload-target="expandIcon"] svg')
      if (icon) {
        icon.classList.add('rotate-180')
      }
    }

    // Clear any existing red borders from regular fields
    expandedRow.querySelectorAll('.border-red-500').forEach(field => {
      field.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
      field.classList.add('border-gray-300', 'focus:border-indigo-500', 'focus:ring-indigo-500')
    })

    // Clear error styling from TomSelect fields
    expandedRow.querySelectorAll('.ts-control.has-error').forEach(control => {
      control.classList.remove('has-error')
    })

    // Clear red border from primary artists section
    const artistSections = expandedRow.querySelectorAll('.bg-gray-50.p-4.rounded-lg.border')
    artistSections.forEach(section => {
      const heading = section.querySelector('h4')
      if (heading && heading.textContent.trim().startsWith('Primary')) {
        section.classList.remove('border-red-500')
        section.classList.add('border-gray-300')
      }
    })

    // Clear contributor section highlighting
    const contributorSections = expandedRow.querySelectorAll('[data-contributor-section]')
    contributorSections.forEach(section => {
      const heading = section.querySelector('h4')
      if (heading) {
        heading.classList.remove('text-red-700')
      }
      const dropdown = section.querySelector('select')
      if (dropdown && dropdown.tomselect) {
        dropdown.tomselect.control.classList.remove('has-error')
      }
    })

    // Highlight fields with errors
    errors.forEach(error => {
      if (error.includes('Title')) {
        const titleInput = expandedRow.querySelector('input[name="track[title]"]')
        if (titleInput) {
          this.highlightField(titleInput)
        }
      }
      if (error.includes('Audio Language')) {
        const langSelect = expandedRow.querySelector('select[name="track[track_language]"]')
        if (langSelect) {
          // Check if this is a TomSelect field
          if (langSelect.tomselect) {
            // Add error class to TomSelect control
            langSelect.tomselect.control.classList.add('has-error')
          } else {
            // Fallback for regular select
            this.highlightField(langSelect)
          }
        }
      }
      if (error.includes('P-Line')) {
        const pLineInput = expandedRow.querySelector('input[name="track[p_line_copyright]"]')
        if (pLineInput) {
          this.highlightField(pLineInput)
        }
      }
      if (error.includes('C-Line')) {
        const cLineInput = expandedRow.querySelector('input[name="track[c_line_copyright]"]')
        if (cLineInput) {
          this.highlightField(cLineInput)
        }
      }
      if (error.includes('primary artist')) {
        // Find the primary artists container - it's a bg-gray-50 div with heading "Primary"
        const artistSections = expandedRow.querySelectorAll('.bg-gray-50.p-4.rounded-lg.border')
        artistSections.forEach(section => {
          const heading = section.querySelector('h4')
          if (heading && heading.textContent.trim().startsWith('Primary')) {
            // Add red border to the container
            section.classList.remove('border-gray-300')
            section.classList.add('border-red-500')
          }
        })
      }
      if (error.includes('Performing contributor')) {
        const performingSection = expandedRow.querySelector('[data-contributor-section="performing"]')
        if (performingSection) {
          this.highlightContributorSection(performingSection)
        }
      }
      if (error.includes('Composition') || error.includes('Lyrics')) {
        const compositionSection = expandedRow.querySelector('[data-contributor-section="composition"]')
        if (compositionSection) {
          this.highlightContributorSection(compositionSection)
        }
      }
      if (error.includes('Production') || error.includes('Engineering')) {
        const productionSection = expandedRow.querySelector('[data-contributor-section="production"]')
        if (productionSection) {
          this.highlightContributorSection(productionSection)
        }
      }
    })
  }

  highlightField(field) {
    // Regular inputs - add red border classes
    field.classList.remove('border-gray-300', 'focus:border-indigo-500', 'focus:ring-indigo-500')
    field.classList.add('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
  }

  highlightContributorSection(section) {
    // Add red border and background to the section heading
    const heading = section.querySelector('h4')
    if (heading) {
      heading.classList.add('text-red-700')
    }
    // Add red border to the roles container dropdown
    const dropdown = section.querySelector('select')
    if (dropdown && dropdown.tomselect) {
      dropdown.tomselect.control.classList.add('has-error')
    }
  }
}